import { setResults } from "../ResultsSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { getFeatureFlag } from "../hooks/useFeatureFlag";
import type { ChatDispatch } from "./chatSliceTypes";

interface RestoredCounts {
  facts: number;
  docs: number;
}

const NOTHING_RESTORED: RestoredCounts = { facts: 0, docs: 0 };

/** Non-array or empty values both degrade to [] so callers can just read length. */
const asList = (value: unknown): unknown[] =>
  Array.isArray(value) && value.length > 0 ? value : [];

/**
 * Restore one persisted results_payload. setResults merges its facts into
 * state.facts and its documents into the document registry; state.documents is
 * replaced, but latestWorkspacePayload overwrites that afterwards.
 */
function restoreOutput(turnId: string, output: any, dispatch: ChatDispatch): RestoredCounts {
  if (output.outputType !== "results_payload" || !output.content) return NOTHING_RESTORED;

  const facts = asList(output.content.facts);
  const documents = asList(output.content.documents);
  const researchDocs = asList(output.content.researchDocs);
  if (facts.length === 0 && documents.length === 0 && researchDocs.length === 0) {
    return NOTHING_RESTORED;
  }

  dispatch(
    setResults({
      facts: facts.length > 0 ? facts : undefined,
      documents: documents.length > 0 ? documents : undefined,
      researchDocs: researchDocs.length > 0 ? researchDocs : undefined,
      results: output.content.results,
      turnId: turnId,
      inlineDealsFlag: getFeatureFlag("inlineDeals"),
    })
  );

  return { facts: facts.length, docs: documents.length };
}

/**
 * Phase 3a: rehydrate ALL historical facts and documents from outputsByTurn, so
 * prior-turn facts can render inline badges and their documents are in the
 * registry for citations to resolve against.
 */
export function restoreHistoricalOutputs(outputsByTurn: any, dispatch: ChatDispatch): void {
  if (!outputsByTurn || typeof outputsByTurn !== "object") return;

  let totalHistoricalFacts = 0;
  let totalHistoricalDocs = 0;

  for (const [turnId, outputs] of Object.entries(outputsByTurn)) {
    if (!Array.isArray(outputs)) continue;

    for (const output of outputs) {
      const restored = restoreOutput(turnId, output, dispatch);
      totalHistoricalFacts += restored.facts;
      totalHistoricalDocs += restored.docs;
    }
  }

  console.log("🔄 [REHYDRATE] Restored historical facts and documents from outputsByTurn:", {
    turnsWithOutputs: Object.keys(outputsByTurn).length,
    totalFacts: totalHistoricalFacts,
    totalDocs: totalHistoricalDocs,
  });
}

function logWorkspacePayload(payload: any): void {
  console.log("🔄 [REHYDRATE] Restoring workspace from latestWorkspacePayload:", {
    turnId: payload.turnId,
    source: payload.source,
    resultsCount: asList(payload.results).length,
    documentsCount: asList(payload.documents).length,
    researchDocsCount: asList(payload.researchDocs).length,
    factsCount: asList(payload.facts).length,
  });
}

/**
 * Which layout the restored evidence calls for, or null to leave the layout
 * alone — deals-only evidence with the inline deals flag ON stays put, since
 * deals now render in the transcript.
 */
function workspaceLayout(payload: any, inlineDealsFlag: boolean): "chat" | "workspace" | null {
  const hasEvidence =
    asList(payload.results).length +
      asList(payload.documents).length +
      asList(payload.facts).length >
    0;
  if (!hasEvidence) return "chat";

  // With the flag ON only facts (doc Q&A) open the workspace; documents
  // alongside deals are reachable from the deal detail panel.
  if (!inlineDealsFlag || asList(payload.facts).length > 0) return "workspace";
  return null;
}

/**
 * Phase 3b: rehydrate the workspace from latestWorkspacePayload — the
 * authoritative snapshot of what the panel should show. Its facts merge with
 * the historical facts restored above.
 */
export function restoreWorkspacePayload(payload: any, dispatch: ChatDispatch): void {
  if (!payload) {
    // Genuinely no workspace data. Do NOT fabricate placeholder deals — stay in
    // chat mode per project guidelines.
    console.log("🔄 [REHYDRATE] No latestWorkspacePayload found - staying in chat mode");
    dispatch(setLayoutMode("chat"));
    return;
  }

  logWorkspacePayload(payload);

  const inlineDealsFlag = getFeatureFlag("inlineDeals");
  dispatch(
    setResults({
      results: payload.results || [],
      documents: payload.documents || [],
      researchDocs: payload.researchDocs || [],
      facts: payload.facts || [],
      turnId: payload.turnId,
      inlineDealsFlag,
    })
  );

  const layout = workspaceLayout(payload, inlineDealsFlag);
  if (layout) {
    dispatch(setLayoutMode(layout));
  }
}
